import { useState } from 'react';
import { Activity, CheckCircle2, Flame, UserCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardOverview } from '@/hooks/use-dashboard';
import { useOverviewAnalytics } from '@/hooks/use-analytics';
import { useAuthContext } from '@/contexts/auth-context';
import { StatsCards } from '@/components/dashboard/stats-cards';
import { KpiCard } from '@/components/analytics/kpi-card';
import { TimeRangeTabs } from '@/components/analytics/time-range-tabs';
import { ActivityLineChart } from '@/components/analytics/activity-line-chart';
import { AssignmentsBarChart } from '@/components/analytics/assignments-bar-chart';
import { CategoryDistributionChart } from '@/components/analytics/category-distribution-chart';
import { LeaderboardTable } from '@/components/analytics/leaderboard-table';
import { formatPercentRate } from '@/lib/analytics-format';
import type {
  RangeParam,
  StudentLeaderboardEntry,
  TeacherLeaderboardEntry,
} from '@/types/analytics';

export default function DashboardPage() {
  const { user } = useAuthContext();
  const [range, setRange] = useState<RangeParam>('30d');

  const { data: overview, isLoading: overviewLoading } = useDashboardOverview();
  const { data: analytics, isLoading: analyticsLoading } = useOverviewAnalytics(range);

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      <StatsCards data={overview} isLoading={overviewLoading} />

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Analytics</h2>
        <TimeRangeTabs value={range} onChange={setRange} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Active today"
          value={analytics?.kpis.active_today ?? '—'}
          subtitle="distinct practicing students"
          icon={UserCheck}
          isLoading={analyticsLoading}
        />
        <KpiCard
          title="Active (7d)"
          value={analytics?.kpis.active_7d ?? '—'}
          icon={Activity}
          isLoading={analyticsLoading}
        />
        <KpiCard
          title="Sessions in range"
          value={analytics?.kpis.sessions_in_range ?? '—'}
          subtitle={`${range}`}
          icon={CheckCircle2}
          isLoading={analyticsLoading}
        />
        <KpiCard
          title="Completion rate"
          value={
            analytics ? formatPercentRate(analytics.kpis.overall_completion_rate) : '—'
          }
          subtitle={`avg streak ${analytics?.kpis.avg_current_streak.toFixed(1) ?? '—'}d`}
          icon={Flame}
          isLoading={analyticsLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Session activity</CardTitle>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : (
              <ActivityLineChart data={analytics?.daily_activity ?? []} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assignments: created vs completed</CardTitle>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : (
              <AssignmentsBarChart data={analytics?.assignments_series ?? []} />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Categories in range</CardTitle>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : (
              <CategoryDistributionChart
                data={analytics?.category_distribution ?? []}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top active students</CardTitle>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : (
              <LeaderboardTable<StudentLeaderboardEntry>
                rows={analytics?.top_students ?? []}
                getKey={(r) => r.student_id}
                getHref={(r) => `/students/${r.student_id}`}
                emptyLabel="No active students in this range"
                columns={[
                  {
                    header: 'Student',
                    cell: (r) => (
                      <span className="font-medium">
                        {r.display_name || r.username || '—'}
                      </span>
                    ),
                  },
                  {
                    header: 'Sessions',
                    cell: (r) => r.sessions_done,
                    className: 'text-right tabular-nums',
                  },
                  {
                    header: 'Completion',
                    cell: (r) => formatPercentRate(r.completion_rate),
                    className: 'text-right tabular-nums',
                  },
                  {
                    header: 'Streak',
                    cell: (r) => `${r.current_streak}d`,
                    className: 'text-right tabular-nums',
                  },
                ]}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {isAdmin && (analytics?.top_teachers ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top active teachers</CardTitle>
          </CardHeader>
          <CardContent>
            <LeaderboardTable<TeacherLeaderboardEntry>
              rows={analytics?.top_teachers ?? []}
              getKey={(r) => r.teacher_id}
              getHref={(r) => `/teachers/${r.teacher_id}`}
              columns={[
                {
                  header: 'Teacher',
                  cell: (r) => <span className="font-medium">{r.display_name || '—'}</span>,
                },
                {
                  header: 'Active students',
                  cell: (r) => r.students_active,
                  className: 'text-right tabular-nums',
                },
                {
                  header: 'Assignments',
                  cell: (r) => r.assignments_created,
                  className: 'text-right tabular-nums',
                },
                {
                  header: 'Cohort completion',
                  cell: (r) => formatPercentRate(r.cohort_completion_rate),
                  className: 'text-right tabular-nums',
                },
              ]}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
